/* eslint-disable @typescript-eslint/no-use-before-define */
// Abstract base ↔ concrete subclass references below are intentional and safe
// under class hoisting — every reference site runs after both classes are fully
// initialized.
import { case as case_, int32, union, void as voidType } from "@stellar/js-xdr";
import { XdrError, type XdrType } from "@stellar/js-xdr";
import { XdrValue } from "../values/xdr-value.js";

export type InnerTransactionResultExtWire = { v: 0 };

export type InnerTransactionResultExtVariantName = "v0";

/**
 * ```xdr
 * union switch (int v)
 *     {
 *     case 0:
 *         void;
 *     }
 * ```
 */
abstract class InnerTransactionResultExtBase extends XdrValue {
  abstract readonly type: InnerTransactionResultExtVariantName;

  constructor() {
    super();
    // `new.target`, not an unconditional throw: every arm subclass reaches
    // this constructor through `super()`, void arms via an implicit one
    if (new.target === InnerTransactionResultExtBase) {
      throw new TypeError(
        "new xdr.InnerTransactionResultExt(...) is not supported: XDR unions are built from " +
          "per-variant factories. Call xdr.InnerTransactionResultExt.v0() " +
          "(or another arm factory) instead.",
      );
    }
  }

  static readonly schema: XdrType<InnerTransactionResultExtWire> = union(
    "InnerTransactionResultExt",
    {
      switchOn: int32(),
      cases: [case_("v0", 0, voidType())],
      switchKey: "v",
    },
  );

  static v0(): InnerTransactionResultExtV0 {
    return new InnerTransactionResultExtV0();
  }

  static fromXdrObject(
    wire: InnerTransactionResultExtWire,
  ): InnerTransactionResultExt {
    switch (wire.v) {
      case 0:
        return new InnerTransactionResultExtV0();
    }
    // unreachable for a well-typed wire object; a hand-built one can still
    // carry an out-of-range discriminant
    throw new XdrError(
      `InnerTransactionResultExt: unknown v ${(wire as { v: unknown }).v}`,
    );
  }

  /**
   * Type guard narrowing an unknown value to a concrete InnerTransactionResultExt variant.
   * Use this instead of `instanceof InnerTransactionResultExt`: the exported `InnerTransactionResultExt` value
   * is the abstract base, so `instanceof` narrows to the base (not the
   * variant union) and forces a cast. `InnerTransactionResultExt.is(x)` narrows to the union.
   */
  static is(value: unknown): value is InnerTransactionResultExt {
    return value instanceof InnerTransactionResultExtBase;
  }

  abstract toXdrObject(): InnerTransactionResultExtWire;
}

export class InnerTransactionResultExtV0 extends InnerTransactionResultExtBase {
  readonly type = "v0" as const;

  get value(): null {
    return null;
  }

  toXdrObject(): Extract<InnerTransactionResultExtWire, { v: 0 }> {
    return { v: 0 };
  }
}

export type InnerTransactionResultExt = InnerTransactionResultExtV0;
export const InnerTransactionResultExt = InnerTransactionResultExtBase;
